import { useState } from "react";
import { useTheme, useMediaQuery, Dialog, Grid2 } from "@mui/material";
import { Datepicker, locale, MbscPopupButton } from "@mobiscroll/react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { BookingTimeTemp } from "../form-edit-time-dialog.component";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";

type P = {
  tempTime: BookingTimeTemp;
  handleChange: (time: BookingTimeTemp) => void;
  open: boolean;
  setOpen: (o: boolean) => void;
  timezone: string;
};

export function BookingTimeEditFieldDialog({
  tempTime,
  handleChange,
  open,
  setOpen,
  timezone
}: Readonly<P>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { timeFormat } = useTimeFormat();

  const [editTime, setEditTime] = useState({
    valueBefore: {
      start: tempTime.bookingStart ?? "",
      end: tempTime.bookingEnd ?? ""
    },
    value: {
      start: tempTime.bookingStart ?? "",
      end: tempTime.bookingEnd ?? ""
    }
  });

  const cancelChange = () => {
    handleChange({
      bookingStart: editTime.valueBefore.start,
      bookingEnd: editTime.valueBefore.end
    });
    setOpen(false);
  };
  const confirmChangesOnClose = () => {
    handleChange({
      bookingStart: editTime.value.start,
      bookingEnd: editTime.value.end
    });
    setOpen(false);
  };

  const timePickingButtons: MbscPopupButton[] = [
    {
      name: "cancel",
      handler: cancelChange,
      text: t("_datePickerButtonsCancel")
    },
    {
      name: "set",
      handler: confirmChangesOnClose,
      text: t("_datePickerButtonsSet")
    }
  ];

  const changeEditTime = (firstValue: Date, secondValue: Date) => {
    const newStart = DateTime.fromJSDate(firstValue).setZone(timezone).toFormat("HH:mm");
    const newEnd = DateTime.fromJSDate(secondValue).setZone(timezone).toFormat("HH:mm");

    setEditTime(e => ({ ...e, value: { start: newStart, end: newEnd } }));
  };

  return (
    <Dialog open={open} hideBackdrop onClose={() => setOpen(false)}>
      <Grid2
        display={"none"} // hide date input field
        data-testid="booking-time-edit-field-parent"
      >
        <Datepicker
          display={"center"}
          select={"range"}
          data-testid={"booking-time-edit-field-picker"}
          controls={["time"]}
          touchUi={isMobile}
          buttons={isMobile ? timePickingButtons : []}
          timeFormat={timeFormat}
          isOpen={open}
          locale={locale[i18n.language]}
          onClose={confirmChangesOnClose}
          onTempChange={picker => {
            // when browser prohibit temp change
            if (!isMobile) return;
            if (picker.value && Array.isArray(picker.value)) {
              changeEditTime(picker.value[0] as Date, picker.value[1] as Date);
            }
          }}
          onChange={picker => {
            if (picker.value && Array.isArray(picker.value)) {
              changeEditTime(picker.value[0] as Date, picker.value[1] as Date);
            }
          }}
          value={[editTime.value.start, editTime.value.end]}
          stepMinute={15}
        />
      </Grid2>
    </Dialog>
  );
}
